import React, { useState, useRef, useEffect } from 'react'
import { GoogleMap, LoadScriptNext, Polygon, Marker, OverlayView, Polyline } from "@react-google-maps/api";
import { Coordinates } from '../../components/Site Functions/LoadSiteData'
import { defaultCenter } from '../../components/Site Functions/LoadSiteDetailsData'
import yellowMarker from '../../assets/mapLocationMarkers/pinYellow.png';
import redMarker from '../../assets/mapLocationMarkers/pinRed.png';
import towerIcon from '../../assets/mapLocationMarkers/cell-tower-AG.png';
import { APIGetLoggerSummaryModel } from "models/APIGetLoggerSummaryModel";
import { CreateUrl, CallGetAPI } from 'Utils/ApiHelper.js';
import { APIGetLoggerEventsModel } from "models/APIGetLoggerEventsModel";
import { FormattedMessage, useIntl, IntlShape } from 'react-intl';

interface Props {
    mapKey: string
    loggerData: APIGetLoggerSummaryModel
    cellInfo: APIGetLoggerEventsModel
    showArea: boolean
}



interface MapMarker {
    position: Coordinates
    icon: string
    label: string
}

interface CellTower {
    accuracyMetres: number
    bandNumber: number
    cellId: number
    cellTowerLocationId: number
    createdAt: Date
    lastVerifiedAt: Date
    locationAreaCode: number
    mobileCountryCode: number
    mobileNetworkCode: number
    towerLatitude: number
    towerLongitude: number
}

interface CellTowerSignalParameters {
    bandNumber: number;
    actType: number; // 7 = LTE, 9 = NB-IoT
}

const GoogleMapsCellTower: React.FC<Props> = ({ mapKey, loggerData, cellInfo, showArea }) => {

    const intl:IntlShape = useIntl()
    ///constants

    const BAND_RADIUS_KM: Record<number, number> = {
        3: 2,
        7: 1,
        8: 5,
        20: 10,
        28: 15,
    };

    const ACT_TYPE_RADIUS_MULTIPLIERS: Record<number, number> = {
        7: 1,
        9: 1.75,
    };


    const POLYGON_POINT_COUNT = 32;
    const EARTH_RADIUS_KM = 6371;
    const DEFAULT_BAND_RADIUS_KM = 5;
    const DEFAULT_ACT_TYPE_MULTIPLIER = 1;


    ///ref
    const mapRef = useRef(null);




    //state
    const [center, setCenter] = useState<Coordinates>(defaultCenter)
    const [markers, setMarkers] = useState<Array<MapMarker>>([])
    const [polygonPath, setPolygonPath] = useState<Coordinates[]>([])
    const [polygonLine, setPolygonLine] = useState<Coordinates[]>([])
    const [distance, setDistance] = useState<number>(0)
    const [noGPS, setNoGPS] = useState<boolean>(false)

    const containerStyle = {
        height: "620px",
        width: "100%",
        marginLeft: "5px",
        minHeight: "500px",
    };

    const onMapLoad = (map):void => {
        mapRef.current = map;
        map.setMapTypeId("hybrid")
        if (mapRef.current) {
            mapRef.current.setZoom(18)
        }
        //fitBoundsToMarkers(); // Fit bounds after the map loads

        //if (!hasBoundary && markers.length === 1) {
        //    setTimeout(() => {
        //        mapRef.current.setZoom(18)
        //    }, 500)
        //}
    };

    const fitBoundsToMarkers = () => {

        if (mapRef.current && markers.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            markers.forEach((marker) => {
                bounds.extend(marker.position);
                if (showArea) {
                    polygonPath.forEach((ply) => {
                        bounds.extend(ply)
                    })
                }
            });
            
            mapRef.current.fitBounds(bounds);
        }

    }

    const calculateCellTowerRadiusKm = (parameters: CellTowerSignalParameters): number => {
        const bandRadiusKm = BAND_RADIUS_KM[parameters.bandNumber] ?? DEFAULT_BAND_RADIUS_KM;
        const actTypeMultiplier = ACT_TYPE_RADIUS_MULTIPLIERS[parameters.actType] ?? DEFAULT_ACT_TYPE_MULTIPLIER;
        return bandRadiusKm * actTypeMultiplier;
    }

    const generateCellTowerCoveragePolygon = (towerLocation: Coordinates, parameters: CellTowerSignalParameters):void => {
        if (towerLocation.lat && towerLocation.lng) {
            const radiusKm = calculateCellTowerRadiusKm(parameters);
            const latRadians = (towerLocation.lat * Math.PI) / 180;
            const angularRadius = radiusKm / EARTH_RADIUS_KM;

            const polygon = Array.from({ length: POLYGON_POINT_COUNT }, (_, pointIndex) => {
                const bearing = (2 * Math.PI * pointIndex) / POLYGON_POINT_COUNT;

                const pointLatRadians = Math.asin(
                    Math.sin(latRadians) * Math.cos(angularRadius) +
                    Math.cos(latRadians) * Math.sin(angularRadius) * Math.cos(bearing)
                );

                const pointLngRadians =
                    (towerLocation.lng * Math.PI) / 180 +
                    Math.atan2(
                        Math.sin(bearing) * Math.sin(angularRadius) * Math.cos(latRadians),
                        Math.cos(angularRadius) - Math.sin(latRadians) * Math.sin(pointLatRadians)
                    );

                return {
                    lat: (pointLatRadians * 180) / Math.PI,
                    lng: (pointLngRadians * 180) / Math.PI,
                };
            });

            setPolygonPath(polygon)
        }
    }

    useEffect(() => {
        console.log('hitting useffect in map')
        //if (cellInfo.details) {
        //    const d = cellInfo.details

        //    ///cellID
        //    const cellIdIdx = d.indexOf('CellId')
        //    const tacIdx = d.indexOf('TAC')
        //    const cellId = parseInt(d.slice(cellIdIdx + 8, tacIdx - 1))

        //    ///countrycode
        //    const mncIdx = d.indexOf('MNC')
        //    const actIdx = d.indexOf('AcT')
        //    const mcc = parseInt(d.slice(mncIdx + 5, actIdx -3))
        //    const mnc = parseInt(d.slice(mncIdx + 8, actIdx -1))

        //    ///tac
        //    const bandIdx = d.indexOf('Band')
        //    const tac = parseInt(d.slice(tacIdx + 5, bandIdx - 1))


        //    //band
        //    const iccIdIdx = d.indexOf('Sim ICCID')
        //    const band = d.slice(bandIdx + 6, iccIdIdx - 1)


        //    CallGetAPI(CreateUrl(`/api/celltowerlocation/getcelltowerlocation?cellId=${cellId}&locationAreaCode=${tac}&mobileCountryCode=${mcc}&mobileNetworkCode=${mnc}&bandNumber=${band}`), {})
        //        .then((data: CellTower) => {
        //            if (data) {
        console.log(loggerData.GpsLatitude, loggerData.GpsLongitude)
        if (loggerData.GpsLatitude && loggerData.GpsLongitude) {
            const markersTemp: MapMarker[] = [
                {
                    position: { lat: loggerData.GpsLatitude, lng: loggerData.GpsLongitude },
                    icon: yellowMarker,
                    label: `${intl.formatMessage({ id: 'Serial' })}: ${loggerData.Serial}`
                }
                //{
                //    position: { lat: data.towerLatitude, lng: data.towerLongitude },
                //    icon: towerIcon,
                //    label: `${intl.formatMessage({ id: 'CellId' })}: ${data.cellId} - ${intl.formatMessage({ id: 'Accuracy' })}: +/- ${Math.floor(data.accuracyMetres)} m`
                //}
            ]
            //generateCellTowerCoveragePolygon({ lat: data.towerLatitude, lng: data.towerLongitude }, { band: 20, actType: 9 })
            //const distanceFromTower = calculateDistanceBetweenCoordinatesKm(
            //    { lat: data.towerLatitude, lng: data.towerLongitude },
            //    { lat: loggerData.GpsLatitude, lng: loggerData.GpsLongitude }
            //)
            //setDistance(distanceFromTower)
            setMarkers(markersTemp)
            //setPolygonLine([{ lat: loggerData.GpsLatitude, lng: loggerData.GpsLongitude }, { lat: data.towerLatitude, lng: data.towerLongitude }])
            setCenter({ lat: loggerData.GpsLatitude, lng: loggerData.GpsLongitude })
            setNoGPS(false)

        } else {
            const markersTemp: MapMarker[] = [
                {
                    position: { lat: defaultCenter.lat, lng: defaultCenter.lng },
                    icon: redMarker,
                    label: `${intl.formatMessage({ id: 'Serial' })}: ${loggerData.Serial}`
                }

            ]
            setNoGPS(true)
            setMarkers(markersTemp)
            setCenter({ lat: defaultCenter.lat, lng: defaultCenter.lng })
        }
        //            }
        //        })
        //        .catch(err => console.error(err))

        //}
    }, [loggerData])

    useEffect(() => {
        if (markers.length > 0) {
            fitBoundsToMarkers()
            
        }
     },
    [markers, showArea])


    const calculateDistanceBetweenCoordinatesKm = (
        pointA: { lat: number; lng: number },
        pointB: { lat: number; lng: number }
    ): number => {
        const pointALatRadians = (pointA.lat * Math.PI) / 180;
        const pointBLatRadians = (pointB.lat * Math.PI) / 180;

        const latDifferenceRadians = ((pointB.lat - pointA.lat) * Math.PI) / 180;
        const lngDifferenceRadians = ((pointB.lng - pointA.lng) * Math.PI) / 180;

        const haversineA =
            Math.sin(latDifferenceRadians / 2) * Math.sin(latDifferenceRadians / 2) +
            Math.cos(pointALatRadians) *
            Math.cos(pointBLatRadians) *
            Math.sin(lngDifferenceRadians / 2) *
            Math.sin(lngDifferenceRadians / 2);

        const haversineC = 2 * Math.atan2(Math.sqrt(haversineA), Math.sqrt(1 - haversineA));

        return EARTH_RADIUS_KM * haversineC;
    }


    return (
        <div style={{ width: "100%" }}>
            {mapKey && <LoadScriptNext googleMapsApiKey={mapKey}>
                <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={center}
                    onLoad={onMapLoad}
                    options={{
                        //styles: mapStyles,
                        disableDefaultUI: false
                    }}
                >
                    {polygonPath && polygonPath.length > 0 && <Polygon
                        paths={polygonPath}
                        options={{
                            editable: false,
                            draggable: false,
                            strokeColor: '#eed202',
                            //strokeOpacity: 0.8,
                            strokeWeight: 2,
                            fillOpacity: 0
                        }}
                    />}
                    {polygonLine && polygonLine.length > 1 && <Polyline
                        path={polygonLine}
                        options={{
                            strokeColor: '#eed202',
                            strokeWeight: 2,
                            strokeOpacity: 0,
                            icons: [{
                                icon: {
                                    path: 'M 0,-1 0,1',
                                    strokeOpacity: 1,
                                    scale: 2
                                },
                                offset: '0',
                                repeat: '10px'
                            }]
                        }}
                    />}
                    {markers.length > 0 && markers.map((marker: MapMarker, idx: number) => {
                        return (
                            <React.Fragment key={idx}>
                                <Marker
                                    position={marker.position}
                                    icon={marker.icon}
                                    label={{
                                        text: marker.label,
                                        color: noGPS ? '#ffffff' : '#eed202',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        className: 'cell-tower-distance-label-tower'
                                        
                                    }}
                                />
                            </React.Fragment>
                        )
                    }
                    )}
                    {markers.length > 1 && <Marker
                        position={{ lat: (markers[0].position.lat + markers[1].position.lat) / 2, lng: (markers[0].position.lng + markers[1].position.lng) / 2 }}
                        icon={{
                            path: window.google?.maps?.SymbolPath.CIRCLE,
                            scale: 0,
                        }}
                        label={{
                            text: `~${distance.toFixed(2)}km`,
                            color: '#eed202',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            className: 'cell-tower-distance-label'
                        }}
                    />}
                </GoogleMap>
            </LoadScriptNext>}
        </div>
    )
}
export default GoogleMapsCellTower